/**
 * Preço à vista (Pix) — adaptador de apresentação.
 *
 * Espelha snippets/pix-amount.liquid. Se a regra mudar lá (depois de medir o
 * arredondamento real no checkout da Yampi), ela precisa mudar aqui também:
 * as duas implementações têm que produzir o mesmo centavo para o mesmo preço.
 *
 * Este arquivo NUNCA altera variant.price, o input name="id", o carrinho ou o
 * payload enviado à Yampi. Ele só reescreve o texto do preço já derivado.
 */
(() => {
  const DISCOUNT_PERCENT = 8;
  const ROUNDING = 'ceil'; // 'ceil' | 'half_up' | 'half_even' | 'floor'

  const COMPLEMENT = 100 - DISCOUNT_PERCENT;
  const subscribed = new Set();

  function pixAmount(baseCents) {
    const cents = Number(baseCents);
    if (!Number.isSafeInteger(cents) || cents < 0) return null;

    const scaled = cents * COMPLEMENT;
    if (!Number.isSafeInteger(scaled)) return null;

    let result = Math.floor(scaled / 100);
    const remainder = scaled % 100;

    switch (ROUNDING) {
      case 'ceil':
        if (remainder > 0) result += 1;
        break;
      case 'half_up':
        if (remainder >= 50) result += 1;
        break;
      case 'half_even':
        if (remainder > 50 || (remainder === 50 && result % 2 === 1)) result += 1;
        break;
      default:
        break;
    }

    return result;
  }

  function formatPrice(cents) {
    const format = window.MinimogSettings?.money_format;
    if (typeof window.formatMoney === 'function' && format) {
      return window.formatMoney(cents, format);
    }

    const currency = window.MinimogSettings?.currency_code || 'BRL';
    const locale = window.MinimogSettings?.shop_locale?.current || 'pt-BR';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  }

  function updateContainer(container, basePrice) {
    const pixPrice = pixAmount(basePrice);
    if (pixPrice === null) return;

    const value = container.querySelector('[data-pix-price-value]');
    if (!value) return;

    container.dataset.basePrice = String(basePrice);
    container.dataset.pixPrice = String(pixPrice);

    let text = formatPrice(pixPrice);
    // Um card que mostrava "A partir de X" deixa de ser uma faixa assim que uma
    // variante concreta é escolhida.
    if (container.dataset.pixShowFrom === 'true') {
      delete container.dataset.pixShowFrom;
    }
    value.innerHTML = text;
  }

  function updateProduct(productId, basePrice) {
    document
      .querySelectorAll(`[data-pix-product-id="${productId}"]`)
      .forEach((container) => updateContainer(container, basePrice));
  }

  function updateWithin(root, basePrice) {
    root
      .querySelectorAll('[data-pix-price-container]')
      .forEach((container) => updateContainer(container, basePrice));
  }

  /**
   * A troca de variante na página de produto e no quick view é publicada pelo
   * próprio tema. Preferimos escutar esse evento a reimplementar a seleção de
   * variantes ou a editar o variant-picker.js minificado do fornecedor.
   */
  function subscribeContainers(root = document) {
    if (!window.MinimogEvents?.subscribe) return false;

    const containers = [];
    if (root.matches?.('[data-pix-product-id]')) containers.push(root);
    root.querySelectorAll?.('[data-pix-product-id]').forEach((node) => containers.push(node));

    containers.forEach((container) => {
      const productId = container.dataset.pixProductId;
      if (!productId || subscribed.has(productId)) return;

      subscribed.add(productId);
      window.MinimogEvents.subscribe(`${productId}__VARIANT_CHANGE`, (variant) => {
        if (variant?.price !== undefined) updateProduct(productId, Number(variant.price));
      });
    });

    return true;
  }

  // Cards de coleção com swatch de cor: a variante muda sem VARIANT_CHANGE.
  function handleCardSwatch(target) {
    const swatch = target.closest?.('pcard-swatch');
    if (!swatch) return;

    // Quando o tema exibe o menor preço do produto, product-card-swatch.js não
    // atualiza o preço ao trocar o swatch. Espelhamos a mesma guarda para que o
    // preço à vista não fique dessincronizado do preço no cartão.
    if (window.MinimogSettings?.pcard_show_lowest_prices) return;

    window.setTimeout(() => {
      const variant = swatch.currentVariant;
      const card = swatch.closest('.m-product-card');
      if (!variant || !card) return;
      updateWithin(card, Number(variant.price));
    }, 0);
  }

  /**
   * O select do sticky add-to-cart sincroniza a página principal, mas não emite
   * VARIANT_CHANGE de volta. Então o sticky é atualizado a partir do JSON de
   * variantes que o próprio snippet já imprime.
   */
  function handleStickySelect(target) {
    if (!target.matches?.('select[name="id"]')) return;

    const bar = target.closest('.m-sticky-addtocart');
    if (!bar) return;

    const dataNode = bar.querySelector('script[type="application/json"]');
    if (!dataNode) return;

    try {
      const variants = JSON.parse(dataNode.textContent);
      const variant = variants.find((item) => item.id === Number(target.value));
      if (variant) updateWithin(bar, Number(variant.price));
    } catch (error) {
      console.error('[pix-price] não foi possível ler as variantes do sticky add-to-cart.', error);
    }
  }

  function handleInteraction(event) {
    handleCardSwatch(event.target);
    handleStickySelect(event.target);
  }

  function initialize() {
    document.addEventListener('change', handleInteraction);
    document.addEventListener('click', handleInteraction);

    // MinimogEvents é carregado por um script defer; esperamos ele existir.
    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (subscribeContainers() || attempts >= 100) window.clearInterval(timer);
    }, 50);

    // Quick view, busca preditiva e seções de app entram no DOM depois.
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) subscribeContainers(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
